import { Typography } from "antd";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import api from "../api/axios";
import { ChangePill, jumpToRow, useOffscreenMarkedRows } from "../components/spine/ChangePill";
import {
  Marker,
  NotInPlanMarker,
  Spine,
  SubtopicList,
  SubtopicRow,
  TopicGroup,
  TopicHeader,
  TopicName,
  spineStyles,
} from "../components/spine/Spine";
import { useEventAction } from "../contexts/EventContext";
import { useView } from "../contexts/ViewContext";

import styles from "./CalibrationRoadmap.module.css";

const { Title, Text } = Typography;

const ARRIVAL_HIGHLIGHT_MS = 1500;

const markedSubtopicIds = (topics) => {
  const ids = new Set();
  for (const topic of topics) {
    for (const subtopic of topic.subtopics) {
      if (subtopic.change) ids.add(subtopic.id);
    }
  }
  return ids;
};

// The first row whose top sits at or below the scroll container's own top
// edge, and how far below it -- the point `restoreScrollAnchor` puts that
// same row back at after the refreshed rows (possibly taller) render.
const captureScrollAnchor = (container) => {
  if (!container) return null;
  const containerTop = container.getBoundingClientRect().top;

  for (const row of container.querySelectorAll("[data-subtopic-id]")) {
    const offset = row.getBoundingClientRect().top - containerTop;
    if (offset >= 0) return { subtopicId: row.dataset.subtopicId, offset };
  }
  return null;
};

const restoreScrollAnchor = (container, anchor) => {
  if (!container || !anchor) return;
  const row = container.querySelector(
    `[data-subtopic-id="${anchor.subtopicId}"]`,
  );
  if (!row) return;

  const containerTop = container.getBoundingClientRect().top;
  const newOffset = row.getBoundingClientRect().top - containerTop;
  container.scrollTop += newOffset - anchor.offset;
};

const CalibrationSubtopicRow = ({ subtopic, arrived }) => (
  <SubtopicRow
    data-subtopic-id={subtopic.id}
    data-marked={Boolean(subtopic.change)}
    accent={subtopic.change ?? undefined}
    className={`${styles.subtopicRow} ${arrived ? styles.arrivalHighlight : ""}`}
    leading={
      <span className={styles.leadingSlot}>
        {subtopic.included ? (
          <Marker className={styles.markerPlain} />
        ) : (
          <NotInPlanMarker />
        )}
      </span>
    }
    secondary={
      subtopic.included && subtopic.description ? (
        <Text className={spineStyles.subtopicSecondary}>
          {subtopic.description}
        </Text>
      ) : null
    }
  >
    <Text className={spineStyles.subtopicName}>{subtopic.name}</Text>
  </SubtopicRow>
);

const CalibrationTopicGroup = ({ topic, number, arrivedIds }) => {
  const hasIncluded = topic.subtopics.some((subtopic) => subtopic.included);

  return (
    <TopicGroup>
      <TopicHeader>
        <span className={styles.topicNumber}>
          {String(number).padStart(2, "0")}
        </span>
        <div className={styles.topicNameBlock}>
          <TopicName>{topic.name}</TopicName>
          {hasIncluded && topic.description && (
            <Text className={styles.topicDescription}>
              {topic.description}
            </Text>
          )}
        </div>
      </TopicHeader>
      <SubtopicList>
        {topic.subtopics.map((subtopic) => (
          <CalibrationSubtopicRow
            key={subtopic.id}
            subtopic={subtopic}
            arrived={arrivedIds.has(subtopic.id)}
          />
        ))}
      </SubtopicList>
    </TopicGroup>
  );
};

const CalibrationRoadmap = ({ learningJourneyId, view }) => {
  const { setOnAdvance, readView, setView } = useView();
  const contentRef = useRef(null);
  const pendingAnchorRef = useRef(null);
  const isLiveRefreshRef = useRef(false);
  const prevMarkedIdsRef = useRef(null);
  const arrivalTimeoutRef = useRef(null);
  const [arrivedIds, setArrivedIds] = useState(() => new Set());

  // Unregister on unmount so a later, unrelated view's "Continue" click
  // can never fire this calibration's completion call.
  useEffect(() => () => setOnAdvance(null), [setOnAdvance]);

  // Armed on any next-step, whatever its predicate: a completed calibration may
  // still resolve to an advance after its modal flow runs, and the control saying
  // so arrives too late to register this then.
  useEventAction("next-step", () => {
    const viewId = view.view_id;
    setOnAdvance(async () => {
      try {
        await api.post(`/workspace/roadmap-calibration/mark-complete/`, {
          learning_journey_id: learningJourneyId,
          view_id: viewId,
        });
      } catch (error) {
        console.error("Failed to mark roadmap calibration complete:", error);
      }
    });
  });

  // `readView` + `setView`, not `fetchView`: this only wants the server's
  // current state, not a re-broadcast of that state's controls (see the
  // comment on `readView` in ViewContext.jsx). No spinner -- D5 requires the
  // spine to never unmount on refresh, only its rows to update in place.
  useEventAction("refresh-roadmap", async () => {
    pendingAnchorRef.current = captureScrollAnchor(contentRef.current);
    isLiveRefreshRef.current = true;
    setView(await readView());
  });

  // Runs after the refreshed rows commit but before paint, so the anchored
  // row is already back in place on the first frame the user sees.
  useLayoutEffect(() => {
    if (!pendingAnchorRef.current) return;
    restoreScrollAnchor(contentRef.current, pendingAnchorRef.current);
    pendingAnchorRef.current = null;
  }, [view]);

  // Marks stay until the next pivot replaces them (D4); the tint is only for
  // rows that just became marked as a result of a live "refresh-roadmap" --
  // never on first mount or a page reload, where every mark is already old.
  useEffect(() => {
    const currentIds = markedSubtopicIds(view.view_data.topics);
    const wasLiveRefresh = isLiveRefreshRef.current;
    isLiveRefreshRef.current = false;

    if (wasLiveRefresh && prevMarkedIdsRef.current) {
      const arrived = [...currentIds].filter(
        (id) => !prevMarkedIdsRef.current.has(id),
      );
      if (arrived.length > 0) {
        setArrivedIds((prev) => new Set([...prev, ...arrived]));
        clearTimeout(arrivalTimeoutRef.current);
        arrivalTimeoutRef.current = setTimeout(() => {
          setArrivedIds((prev) => {
            const next = new Set(prev);
            arrived.forEach((id) => next.delete(id));
            return next;
          });
        }, ARRIVAL_HIGHLIGHT_MS);
      }
    }

    prevMarkedIdsRef.current = currentIds;
  }, [view]);

  useEffect(() => () => clearTimeout(arrivalTimeoutRef.current), []);

  const offscreenMarked = useOffscreenMarkedRows(contentRef, [view]);

  return (
    <div className={styles.page}>
      <div className={styles.content} ref={contentRef}>
        <Title level={2} className={styles.pageTitle}>
          Your plan
        </Title>
        <Spine>
          {view.view_data.topics.map((topic, index) => (
            <CalibrationTopicGroup
              key={topic.name}
              topic={topic}
              number={index + 1}
              arrivedIds={arrivedIds}
            />
          ))}
        </Spine>
        {offscreenMarked.length > 0 && (
          <ChangePill
            direction={offscreenMarked[0].direction}
            count={offscreenMarked.length}
            onClick={() => jumpToRow(offscreenMarked[0].row)}
          />
        )}
      </div>
    </div>
  );
};

export default CalibrationRoadmap;
